/**
 * Assessment Reports Page
 * AIDLC Administrative Backend
 *
 * Sam's assessment reports -- his analysis each time he wakes up.
 * Shows a latest assessment banner, a scrollable list of past
 * assessments, and an expandable detail view with backlog snapshot,
 * budget snapshot, milestone candidates, and recommendation.
 * Consumes the backend /api/assessments endpoints.
 *
 * Personas: Ops, Orchestrator
 */

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtTokens, fmtDateTime, fmtDate } from '../../utils/formatters';
import type {
  AssessmentReport,
  AssessmentRecommendation,
} from '../../services/orchestrationService';
import {
  getAssessments,
  getLatestAssessment,
  createAssessment,
} from '../../services/orchestrationService';

/** Recommendation badge config */
const recommendationConfig: Record<
  AssessmentRecommendation,
  { label: string; variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral' }
> = {
  ready_to_spawn: { label: 'Ready to Spawn', variant: 'success' },
  needs_estimation: { label: 'Needs Estimation', variant: 'warning' },
  needs_budget: { label: 'Needs Budget', variant: 'danger' },
  needs_prioritization: { label: 'Needs Prioritization', variant: 'info' },
  dormant: { label: 'Dormant', variant: 'neutral' },
};

/** Trigger label (matching backend AssessmentReport.triggeredBy values) */
function triggerLabel(trigger: string): string {
  const labels: Record<string, string> = {
    scheduled: 'Scheduled',
    ceo_directive: 'CEO Directive',
    new_backlog: 'New Backlog',
    agent_completion: 'Agent Completion',
    blocker_detected: 'Blocker Detected',
    budget_replenishment: 'Budget Replenishment',
    milestone_gate: 'Milestone Gate',
  };
  return labels[trigger] || trigger;
}

/** Trigger badge variant */
function triggerBadgeVariant(trigger: string): 'info' | 'warning' | 'success' | 'danger' | 'purple' | 'neutral' {
  switch (trigger) {
    case 'ceo_directive': return 'danger';
    case 'new_backlog': return 'info';
    case 'agent_completion': return 'success';
    case 'blocker_detected': return 'warning';
    case 'budget_replenishment': return 'purple';
    case 'milestone_gate': return 'info';
    case 'scheduled': return 'neutral';
    default: return 'neutral';
  }
}

/** MoSCoW priority color classes */
const priorityColors: Record<string, { bg: string; text: string }> = {
  must: { bg: 'bg-red-50', text: 'text-red-700' },
  should: { bg: 'bg-orange-50', text: 'text-orange-700' },
  could: { bg: 'bg-blue-50', text: 'text-blue-700' },
  wont: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

// ── Main Component ───────────────────────────────────────────────────

export function AssessmentReportsPage() {
  const [assessments, setAssessments] = useState<AssessmentReport[]>([]);
  const [latest, setLatest] = useState<AssessmentReport | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [allData, latestData] = await Promise.allSettled([
        getAssessments(),
        getLatestAssessment(),
      ]);
      if (allData.status === 'fulfilled') {
        setAssessments(allData.value);
      }
      if (latestData.status === 'fulfilled') {
        setLatest(latestData.value);
      }
    } catch (err) {
      console.error('[AssessmentReports] Failed to load:', err);
      setError('Failed to load assessment reports. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateAssessment = async () => {
    setIsCreating(true);
    try {
      await createAssessment({
        triggeredBy: 'scheduled',
        backlogSnapshot: {
          totalItems: 0,
          unestimated: 0,
          estimated: 0,
          byPriority: { must: 0, should: 0, could: 0, wont: 0 },
        },
        budgetSnapshot: {
          totalBudget: 0,
          spent: 0,
          remaining: 0,
          burnRate: 0,
        },
        recommendation: 'dormant',
        notes: 'Manual assessment triggered via UI',
        agentId: 'sam',
      });
      await loadData();
    } catch (err) {
      console.error('[AssessmentReports] Failed to create:', err);
      setError('Failed to create assessment. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading assessment reports..." />;
  }

  if (error && assessments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Assessments</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Assessment Reports</h1>
          <p className="text-sm text-gray-500 mt-1">
            Sam's analysis reports generated each time he wakes up and evaluates the backlog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="md"
            isLoading={isCreating}
            onClick={handleCreateAssessment}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New Assessment
          </Button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Inline error banner */}
      {error && assessments.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Latest Assessment Banner */}
      {latest && <LatestBanner assessment={latest} />}

      {/* Assessment List */}
      {assessments.length === 0 ? (
        <EmptyState
          title="No assessments yet"
          description="No assessment reports have been generated. Trigger a new assessment or wait for Sam's next scheduled wake-up."
          action={
            <Button variant="primary" size="sm" isLoading={isCreating} onClick={handleCreateAssessment}>
              Generate Assessment
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {assessments.map((assessment) => (
            <AssessmentCard
              key={assessment.id}
              assessment={assessment}
              isExpanded={expandedId === assessment.id}
              onToggle={() => toggleExpand(assessment.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Latest Assessment Banner ─────────────────────────────────────────

function LatestBanner({ assessment }: { assessment: AssessmentReport }) {
  const rec = recommendationConfig[assessment.recommendation] || recommendationConfig.dormant;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 ring-1 ring-ldaja-blue/10">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-ldaja-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
        <h2 className="text-sm font-bold text-ldaja-black">Latest Assessment</h2>
        <span className="text-xs text-gray-400">{fmtDateTime(assessment.createdAt)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Badge variant={rec.variant} dot className="text-sm px-3 py-1">
          {rec.label}
        </Badge>
        <Badge variant={triggerBadgeVariant(assessment.triggeredBy)} dot>
          {triggerLabel(assessment.triggeredBy)}
        </Badge>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Total Items</p>
          <p className="text-lg font-bold text-gray-800 mt-1">{assessment.backlogSnapshot.totalItems}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Unestimated</p>
          <p className="text-lg font-bold text-amber-600 mt-1">{assessment.backlogSnapshot.unestimated}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Budget Remaining</p>
          <p className="text-lg font-bold text-green-600 mt-1">{fmtTokens(assessment.budgetSnapshot.remaining)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Burn Rate</p>
          <p className="text-lg font-bold text-ldaja-blue mt-1">{fmtTokens(assessment.budgetSnapshot.burnRate)}/day</p>
        </div>
      </div>
    </div>
  );
}

// ── Assessment Card ──────────────────────────────────────────────────

function AssessmentCard({
  assessment,
  isExpanded,
  onToggle,
}: {
  assessment: AssessmentReport;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const rec = recommendationConfig[assessment.recommendation] || recommendationConfig.dormant;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden transition-shadow hover:shadow-sm">
      {/* Summary row (always visible) */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Date */}
          <div className="shrink-0">
            <p className="text-sm font-medium text-gray-800">{fmtDate(assessment.createdAt)}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{assessment.id.substring(0, 8)}</p>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={triggerBadgeVariant(assessment.triggeredBy)} dot>
              {triggerLabel(assessment.triggeredBy)}
            </Badge>
            <Badge variant={rec.variant} dot>
              {rec.label}
            </Badge>
          </div>

          {/* Stats inline */}
          <div className="hidden sm:flex items-center gap-4 text-xs text-gray-500 ml-auto mr-4">
            <span>
              <span className="text-gray-400">Items: </span>
              <span className="font-medium text-gray-700">{assessment.backlogSnapshot.totalItems}</span>
            </span>
            <span>
              <span className="text-gray-400">Unest: </span>
              <span className="font-medium text-amber-600">{assessment.backlogSnapshot.unestimated}</span>
            </span>
            <span>
              <span className="text-gray-400">Budget: </span>
              <span className="font-medium text-green-600">{fmtTokens(assessment.budgetSnapshot.remaining)}</span>
            </span>
          </div>
        </div>

        {/* Expand chevron */}
        <svg
          className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Detail (expanded) */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4 space-y-5">
          <AssessmentDetail assessment={assessment} />
        </div>
      )}
    </div>
  );
}

// ── Assessment Detail ────────────────────────────────────────────────

function AssessmentDetail({ assessment }: { assessment: AssessmentReport }) {
  const rec = recommendationConfig[assessment.recommendation] || recommendationConfig.dormant;
  const bs = assessment.backlogSnapshot;
  const budg = assessment.budgetSnapshot;
  const budgetPct = budg.totalBudget > 0 ? (budg.remaining / budg.totalBudget) * 100 : 0;
  const spentPct = budg.totalBudget > 0 ? (budg.spent / budg.totalBudget) * 100 : 0;

  return (
    <>
      {/* Backlog Snapshot */}
      <div>
        <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">
          Backlog Snapshot
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <StatCell label="Total Items" value={bs.totalItems} />
          <StatCell label="Unestimated" value={bs.unestimated} valueColor="text-amber-600" />
          <StatCell label="Estimated" value={bs.estimated} valueColor="text-green-600" />
          <StatCell label="Est. Rate" value={bs.totalItems > 0 ? `${Math.round((bs.estimated / bs.totalItems) * 100)}%` : '0%'} valueColor="text-ldaja-blue" />
        </div>

        {/* Priority breakdown */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-400">Priority:</span>
          {(Object.entries(bs.byPriority) as [string, number][]).map(([key, count]) => {
            const colors = priorityColors[key] || priorityColors.could;
            return (
              <span
                key={key}
                className={`text-xs font-medium px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}: {count}
              </span>
            );
          })}
        </div>
      </div>

      {/* Budget Snapshot */}
      <div>
        <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">
          Budget Snapshot
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <StatCell label="Total Budget" value={fmtTokens(budg.totalBudget)} />
          <StatCell label="Spent" value={fmtTokens(budg.spent)} valueColor="text-red-600" />
          <StatCell label="Remaining" value={fmtTokens(budg.remaining)} valueColor="text-green-600" />
          <StatCell label="Burn Rate" value={`${fmtTokens(budg.burnRate)}/day`} valueColor="text-ldaja-blue" />
        </div>

        {/* Budget progress bar */}
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all bg-gradient-to-r from-ldaja-blue to-blue-400"
            style={{ width: `${Math.min(spentPct, 100)}%` }}
            title={`${spentPct.toFixed(1)}% spent`}
          />
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>{spentPct.toFixed(1)}% spent</span>
          <span>{budgetPct.toFixed(1)}% remaining</span>
        </div>
      </div>

      {/* Milestone Candidates */}
      <div>
        <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">
          Milestone Candidates
        </h4>
        {assessment.milestoneCandidates.length === 0 ? (
          <p className="text-xs text-gray-400">No backlog items ready for milestone inclusion.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {assessment.milestoneCandidates.map((itemId) => (
              <span
                key={itemId}
                className="text-[10px] px-2 py-1 bg-blue-50 text-ldaja-blue rounded-md font-mono"
              >
                {itemId}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Sam's Notes */}
      {assessment.notes && (
        <div>
          <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
            Sam's Notes
          </h4>
          <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 leading-relaxed">
            {assessment.notes}
          </p>
        </div>
      )}

      {/* Recommendation */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
          Recommendation:
        </span>
        <Badge variant={rec.variant} dot className="text-sm px-3 py-1">
          {rec.label}
        </Badge>
      </div>
    </>
  );
}

// ── Stat Cell ────────────────────────────────────────────────────────

function StatCell({
  label,
  value,
  valueColor = 'text-gray-800',
}: {
  label: string;
  value: string | number;
  valueColor?: string;
}) {
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-base font-bold mt-1 ${valueColor}`}>{value}</p>
    </div>
  );
}
